/**
 * PDP behaviour that the redesigned layout needs on top of what the theme
 * already ships in product.js.
 *
 * This replaces the AB Tasty "Figma" variation's runtime DOM surgery. Because
 * the theme now renders the final markup itself, almost all of that script's
 * machinery is gone; what remains here is genuine behaviour, not layout
 * patching.
 *
 * Size gate
 *   The design asks for no size to appear pre-selected, and for Add to Bag to
 *   be blocked with a message until the shopper picks one. The underlying
 *   radio state is deliberately left alone — slate.Variants, price rendering
 *   and analytics all resolve from the theme's pre-checked variant — so the
 *   gate is a presentational class plus a capture-phase guard on the add
 *   control, and it lifts on the first real interaction.
 *   Mirrors ab-figma-usp.js L3788-3834.
 */
(function () {
  var GATE_SELECTOR = '[data-pdp-size-gate]';
  var PRISTINE_CLASS = 'pdp-size-pristine';
  /* Turns the chip borders red alongside the message, mirroring the target's
     `ab-figma-size-error-shown` state (ab-figma-usp.css L877-880). */
  var ERROR_CLASS = 'pdp-size-error-shown';
  /**
   * The add control, and ONLY the add control.
   *
   * This list used to include `form.product__form`, which was a defect rather
   * than breadth: `closest()` walks ancestors, so every click anywhere inside
   * the product form matched it. The capture-phase guard then ran
   * preventDefault + stopPropagation on clicks on the size chips themselves —
   * so the radio never got checked, the bubble-phase listener that lifts the
   * gate never fired, and picking a size instead produced the error message.
   * It also swallowed clicks on the wallet button.
   */
  var ADD_SELECTOR = '.pdp_main_product__add, button[name="add"]';

  function isPristine(gate) {
    return gate.classList.contains(PRISTINE_CLASS);
  }

  function errorFor(gate) {
    var scope = gate.closest('.size-option') || gate.parentElement;
    return scope ? scope.querySelector('[data-pdp-size-error]') : null;
  }

  /**
   * The message is shown and hidden by writing its text, not by toggling
   * `hidden`. `role="alert"` is only reliably announced when the content of an
   * already-present live region changes; revealing a hidden node is commonly
   * skipped, which left a screen-reader user with an unexplained focus jump to
   * the first size chip. The copy comes off the element so no user-facing string
   * is hardcoded here.
   */
  function setError(gate, on) {
    var error = errorFor(gate);
    if (!error) return;
    if (!on) {
      error.textContent = '';
      return;
    }

    var message = error.getAttribute('data-pdp-size-error-text') || '';
    if (error.textContent !== message) {
      error.textContent = message;
      return;
    }

    /* Already showing — a second Add to Bag with the same text is not a
       mutation, so nothing would be re-announced. Blank it now and restore it on
       the next frame, which is a real change the live region reports. */
    error.textContent = '';
    var restore = function () { error.textContent = message; };
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(restore);
    } else {
      window.setTimeout(restore, 16);
    }
  }

  function clearGate(gate) {
    if (!isPristine(gate)) return;
    gate.classList.remove(PRISTINE_CLASS);
    gate.classList.remove(ERROR_CLASS);
    gate.removeAttribute('aria-invalid');
    setError(gate, false);
  }

  function showError(gate) {
    gate.classList.add(ERROR_CLASS);
    gate.setAttribute('aria-invalid', 'true');
    setError(gate, true);
  }

  /** The gate only applies while a real, un-chosen size list is on the page. */
  function blockingGate(form) {
    var gate = form
      ? form.querySelector(GATE_SELECTOR)
      : document.querySelector(GATE_SELECTOR);
    if (!gate || !isPristine(gate)) return null;
    // A single-value list (for example a one-size product) has nothing to pick.
    return gate.querySelectorAll('input[type="radio"]').length > 1 ? gate : null;
  }

  /**
   * Deselect: puts the list back exactly as the page loaded it. The gate is
   * pristine again (the chip row reads as unanswered, the Add to Bag guard is
   * re-armed) and any standing error is withdrawn. The radio underneath KEEPS
   * its checked state, precisely as on page entry: the theme's pre-checked
   * variant goes on driving slate.Variants, pricing and add-to-cart while the
   * row shows nothing selected. Unchecking it for real would leave the option
   * with no value at all and break variant resolution.
   */
  function reopenGate(gate) {
    gate.classList.add(PRISTINE_CLASS);
    gate.classList.remove(ERROR_CLASS);
    gate.removeAttribute('aria-invalid');
    setError(gate, false);
  }

  var WAS_CHECKED_ATTR = 'data-pdp-was-checked';

  /**
   * Start of a gesture (pointerdown, or a key press): snapshot every chip
   * radio's checked state and forget the previous gesture's click. Both feed
   * onSelection's deselect decision, see there.
   */
  function onGestureStart(event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var gate = target.closest(GATE_SELECTOR);
    if (!gate) return;
    gate.pdpClickHandled = false;
    var radios = gate.querySelectorAll('input[type="radio"]');
    for (var i = 0; i < radios.length; i++) {
      radios[i].setAttribute(WAS_CHECKED_ATTR, radios[i].checked ? 'true' : 'false');
    }
  }

  /**
   * A real selection lifts the gate; a second click on the chip that is
   * ALREADY selected puts it back (deselect).
   *
   * Whether the click is a deselect hinges on the radio's state BEFORE the
   * click, and where that is read from depends on what the click landed on:
   *   - the label or its span: `radio.checked` is still the pre-click value,
   *     because the browser activates the input (and re-dispatches a click on
   *     it) only after the label's own click has finished bubbling;
   *   - the input itself (the theme leaves it transparent over the chip, so
   *     real clicks can land there): its checkedness is toggled before the
   *     click is dispatched, so the pre-click value is taken from the
   *     pointerdown snapshot instead.
   * On a deselect the click is cancelled, which stops the label's activation
   * and the re-dispatched click. One gesture is handled once: the flag set
   * here makes the re-dispatched click of a plain selection a no-op, so
   * re-selecting the pre-checked chip from pristine does not read as a
   * deselect of itself. `change` (arrow keys, or the activation of a first
   * selection) only ever lifts the gate.
   *
   * Clicks on a sold-out chip are ignored: its radio is `disabled` so no size
   * actually got chosen, and letting the click through would mark the list as
   * answered while the pre-checked variant is still the live one.
   */
  function onSelection(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var label = target.closest(GATE_SELECTOR + ' label');
    if (label && label.classList.contains('pdp-size-unavailable')) return;

    var radio = target.closest(GATE_SELECTOR + ' input[type="radio"]');
    if (radio && radio.disabled) return;

    var gate = target.closest(GATE_SELECTOR);
    if (!gate) return;

    if (event.type === 'click') {
      if (gate.pdpClickHandled) return;
      gate.pdpClickHandled = true;

      if (!isPristine(gate)) {
        var control = radio || (label ? (label.control || label.querySelector('input[type="radio"]')) : null);
        var wasChecked = radio
          ? radio.getAttribute(WAS_CHECKED_ATTR) === 'true'
          : !!(control && control.checked);
        if (control && !control.disabled && wasChecked) {
          event.preventDefault();
          reopenGate(gate);
          return;
        }
      }
    }

    clearGate(gate);
  }

  /** A bubbling custom event; the createEvent path is for engines without the
      CustomEvent constructor. */
  function dispatch(node, name) {
    var event;
    try {
      event = new CustomEvent(name, { bubbles: true });
    } catch (error) {
      event = document.createEvent('CustomEvent');
      event.initCustomEvent(name, true, false, null);
    }
    node.dispatchEvent(event);
  }

  /** Blocks the add and reveals the message; never touches form state. */
  function block(event, gate) {
    event.preventDefault();
    event.stopPropagation();
    showError(gate);

    /* That stopPropagation also keeps the click from product.js's own handler
       on the button, so its gift-card field validation (recipient name, email,
       send date) would never run on a blocked attempt: the shopper saw only
       the amount error, picked an amount, clicked again and only then learnt
       about the other fields. Announcing the block lets product.js validate
       the rest of the form for the SAME attempt, so everything missing shows
       at once. Bubbles from the gate to the section container product.js
       listens on; a plain PDP has no such fields and ignores it. */
    dispatch(gate, 'pdp:add-blocked');

    var firstOption = gate.querySelector('input[type="radio"]:not([disabled])');
    if (firstOption) firstOption.focus();
  }

  function guardAddClick(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var add = target.closest(ADD_SELECTOR);
    if (!add || add.disabled) return;

    var gate = blockingGate(add.closest('form'));
    if (gate) block(event, gate);
  }

  /**
   * Native submits (Enter in a field, or the button when product.js has bailed
   * out of its own click handler) never pass through the click guard.
   */
  function guardSubmit(event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('form.product__form')) return;

    var gate = blockingGate(form);
    if (gate) block(event, gate);
  }

  /**
   * Compact rating jumps to the reviews section and opens it.
   *
   * The reviews section is a collapsed accordion, so scrolling alone can land on
   * a closed card. Expand it first, then scroll with an offset for the sticky
   * header — a plain scrollIntoView puts the heading underneath it.
   */
  function initReviewsJump() {
    var trigger = document.querySelector('[data-pdp-reviews-jump]');
    if (!trigger) return;

    /* Either address of the Reviews section: product.json's fixed "reviews"
       key gives the id-suffix form; an instance the trade team adds through
       the theme editor gets an auto-generated key, so it is found through
       the stable shell class the section stamps on itself instead. */
    function findReviewsSection() {
      var byId = document.querySelector('.shopify-section[id$="__reviews"]');
      if (byId) return byId;
      var shell = document.querySelector('.pdp-reviews-shell');
      return shell ? shell.closest('.shopify-section') : null;
    }

    /* The compact score is a JUMP CONTROL, not a badge: with no Reviews
       section on the page (hidden, removed, or never added to this
       template) there is nowhere to take the shopper, so the whole
       score-plus-star button is hidden even when the product has reviews.
       Inline style, because the button's own stylesheet display would win
       over a [hidden] attribute. */
    if (!findReviewsSection()) {
      trigger.style.display = 'none';
      return;
    }

    trigger.addEventListener('click', function () {
      var section = findReviewsSection();
      if (!section) return;

      var heading = section.querySelector('.pdp-accordion-heading');
      if (heading && heading.getAttribute('aria-expanded') === 'false') {
        heading.click();
      }

      var header = document.querySelector('.site-header-section');
      var offset = header ? Math.round(header.getBoundingClientRect().height) : 0;
      var top =
        section.getBoundingClientRect().top + window.pageYOffset - offset - 12;

      window.scrollTo({
        top: top,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth'
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Add-to-bag feedback: pressed -> spinner -> tick+Added -> rest, plus  */
  /* the dot that flies to the header bag.                                */
  /* ------------------------------------------------------------------ */

  var ATB_SELECTOR = '.pdp_main_product__add';
  var ATB_PRESSED_MS = 180; /* stage 1 length */
  var ATB_LOADING_MIN_MS = 400; /* spinner never flashes shorter than this */
  var ATB_ADDED_MS = 2000; /* how long the tick + "Added" pose holds */
  /* Silent revert if no completion signal ever arrives (request failed, or a
     gift-card form failed its own validation after the click went through).
     The existing error handling owns the messaging; the button just returns
     to rest instead of spinning forever. */
  var ATB_FAILSAFE_MS = 5000;

  /**
   * The dot. Pure Web Animations API on transform + opacity — compositor
   * only, removed on finish. Skipped without a header bag to fly to, without
   * WAAPI, and under prefers-reduced-motion (decorative, unlike the button
   * stages, which are feedback and always run).
   */
  function launchAtbDot(button) {
    if (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }
    /* The VISIBLE bag, not the first in the DOM. The header renders the bag
       twice through site-header-bag.liquid — `shd__` (desktop) and `shm__`
       (mobile) — and only one is shown per breakpoint; the hidden one
       measures 0x0 at the viewport origin. Both wrap the icon in the shared
       `.bag-icon` span, so that one class covers every breakpoint; the two
       block-level anchors stay as fallbacks in case the icon span is ever
       renamed. An earlier version listed only the `shm__` selectors, which
       aimed the dot at the hidden mobile bag's 0x0 rect on desktop first
       ("flies the wrong way"), then — once visibility filtering was added —
       found no candidate at all and skipped the dot entirely. */
    var bag = null;
    var candidates = document.querySelectorAll('.bag-icon, .shd__bag, .shm__bag');
    for (var c = 0; c < candidates.length; c++) {
      var rect = candidates[c].getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        bag = candidates[c];
        break;
      }
    }
    if (!bag) return;

    var dot = document.createElement('span');
    dot.className = 'pdp-atb-dot';
    if (typeof dot.animate !== 'function') return;

    var from = button.getBoundingClientRect();
    var to = bag.getBoundingClientRect();
    /* -5: half the dot's 10px, so the path runs centre to centre. */
    var x0 = from.left + from.width / 2 - 5;
    var y0 = from.top + from.height / 2 - 5;
    var x1 = to.left + to.width / 2 - 5;
    var y1 = to.top + to.height / 2 - 5;

    document.body.appendChild(dot);

    /* The path is a quadratic Bezier SAMPLED into many keyframes, not three
       hand-placed poses. Three keyframes meant two straight segments meeting
       at the apex — a visible kink right before the bag, made worse by an
       apex pinned 40px ABOVE the bag so the dot had to hook down into it.
       Sampling a real curve leaves no corners anywhere, and the control point
       is deliberately gentle: the straight line's midpoint lifted by 12% of
       the flight distance (capped at 48px), so the flight reads as a shallow
       throw rather than a mortar lob. Scale and opacity ride the same
       progress — shrink to 0.4 over the flight, fade out over the last
       quarter. Still transform + opacity only. */
    var dx = x1 - x0;
    var dy = y1 - y0;
    var distance = Math.sqrt(dx * dx + dy * dy);
    var lift = Math.min(48, distance * 0.12);
    var cx = (x0 + x1) / 2;
    var cy = (y0 + y1) / 2 - lift;

    var STEPS = 25;
    var frames = [];
    for (var s = 0; s <= STEPS; s++) {
      var t = s / STEPS;
      var inverse = 1 - t;
      var px = inverse * inverse * x0 + 2 * inverse * t * cx + t * t * x1;
      var py = inverse * inverse * y0 + 2 * inverse * t * cy + t * t * y1;
      frames.push({
        transform:
          'translate3d(' + px.toFixed(1) + 'px,' + py.toFixed(1) + 'px,0)' +
          ' scale(' + (1 - 0.6 * t).toFixed(3) + ')',
        opacity: t < 0.75 ? 1 : Math.max(0, 1 - (t - 0.75) / 0.25)
      });
    }

    var animation = dot.animate(frames, {
      /* 1150ms: 650 read as a flick, too fast to follow from the button to the
         corner of the screen; 950 still crossed a desktop viewport quicker
         than the eye tracks it. The cleanup belt below is timed off this. */
      duration: 1150,
      easing: 'cubic-bezier(0.35, 0.1, 0.25, 1)'
    });

    var cleanup = function () {
      if (dot.parentNode) dot.parentNode.removeChild(dot);
    };
    animation.onfinish = cleanup;
    animation.oncancel = cleanup;
    /* Belt for engines that fire neither callback on a discarded animation.
       Kept clear of the flight duration above. */
    setTimeout(cleanup, 1800);
  }

  /**
   * The button's stage machine. Stages are classes; buttons.scss owns every
   * visual. Completion comes from the REAL request lifecycle: the side cart
   * dispatches `cart:rendered` after a successful cart/add.js + re-render
   * (scripts/components/cart.js), so the tick can never show before the item
   * is actually in the cart. The listener only exists between click and
   * finish, so renders from other causes cannot trigger anything.
   */
  function initAtbFeedback() {
    var active = null;

    function start(button) {
      var entry = { timers: [], done: false, startedAt: Date.now() };
      active = entry;

      var countObserver = null;

      function finish() {
        if (entry.done) return;
        entry.done = true;
        for (var i = 0; i < entry.timers.length; i++) clearTimeout(entry.timers[i]);
        document.removeEventListener('cart:rendered', onSuccessSignal);
        if (countObserver) countObserver.disconnect();
        button.classList.remove('pdp-atb--pressed', 'pdp-atb--loading', 'pdp-atb--added');
        active = null;
      }

      function showAdded() {
        if (entry.done) return;
        button.classList.remove('pdp-atb--pressed', 'pdp-atb--loading');
        button.classList.add('pdp-atb--added');
        entry.timers.push(setTimeout(finish, ATB_ADDED_MS));
      }

      /* Success can be reported by either of two independent signals, first
         one wins:
           - the header bag count changing (the surest sign the add landed —
             every add path repaints it, whichever popup or drawer follows);
           - the side cart's `cart:rendered` event.
         `cart:rendered` ALONE was a real bug: the PDP flow opens the
         added-to-cart modal without necessarily re-rendering the side cart,
         so the event never came, the failsafe quietly reset the button, and
         the shopper saw the modal but never the tick. */
      var signalled = false;
      function onSuccessSignal() {
        if (signalled || entry.done) return;
        signalled = true;
        document.removeEventListener('cart:rendered', onSuccessSignal);
        if (countObserver) countObserver.disconnect();
        /* Hold the spinner to its minimum even when the signal arrives
           faster — a sub-100ms flash reads as a glitch, not as feedback.
           Measured from the click so an instant response also clears the
           pressed stage cleanly. */
        var elapsed = Date.now() - entry.startedAt;
        var wait = Math.max(0, ATB_PRESSED_MS + ATB_LOADING_MIN_MS - elapsed);
        entry.timers.push(setTimeout(showAdded, wait));
      }

      document.addEventListener('cart:rendered', onSuccessSignal);

      if (typeof window.MutationObserver === 'function') {
        var counts = document.querySelectorAll('.shm__bag-count');
        if (counts.length) {
          var baseline = [];
          for (var n = 0; n < counts.length; n++) baseline.push(counts[n].textContent);
          countObserver = new MutationObserver(function () {
            for (var m = 0; m < counts.length; m++) {
              if (counts[m].textContent !== baseline[m]) {
                onSuccessSignal();
                return;
              }
            }
          });
          for (var o = 0; o < counts.length; o++) {
            countObserver.observe(counts[o], {
              childList: true,
              characterData: true,
              subtree: true
            });
          }
        }
      }

      button.classList.add('pdp-atb--pressed');
      entry.timers.push(
        setTimeout(function () {
          if (entry.done || button.classList.contains('pdp-atb--added')) return;
          button.classList.remove('pdp-atb--pressed');
          button.classList.add('pdp-atb--loading');
        }, ATB_PRESSED_MS)
      );
      entry.timers.push(setTimeout(finish, ATB_FAILSAFE_MS));
    }

    /* Bubble phase, document level. A size-gate-blocked click never arrives
       here at all — the gate's guard runs in the CAPTURE phase and calls
       stopPropagation — so everything seen here is a click the add flow will
       actually act on. `defaultPrevented` is deliberately NOT checked:
       product.js prevents the default on every AJAX add, valid ones
       included. */
    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var button = target.closest(ATB_SELECTOR);
      if (!button || button.disabled) return;
      if (active) return;

      launchAtbDot(button);
      /* A button without the feedback layers (a floating clone, quickview)
         still gets the dot, but has nothing to stage. */
      if (!button.querySelector('.pdp-atb__spinner')) return;
      start(button);
    });
  }

  /**
   * Sticky offset for the desktop two-column layout (pdp/layout.scss).
   *
   * The columns pin at `top: var(--pdp-sticky-top, 81px)`. The 81px fallback
   * is the AB variation's constant and is only right for a store whose legacy
   * header is stickybits-pinned (~65px tall + 16px gap). The redesigned shd
   * header is not viewport-pinned at all, and regional stores stack
   * announcement strips of different heights, so the real clearance has to be
   * measured: the bottom edge of whatever header chrome currently overlays
   * the viewport top, plus the same 16px gap (16px on its own when nothing
   * is pinned). Tracked on scroll so a non-pinned header still mid-exit is
   * followed frame by frame; the style write is skipped while the value is
   * stable.
   */
  /* The bottom edge of whatever header chrome currently overlays the
     viewport top (0 when nothing does). Shared by the sticky-column offset
     and the mobile buy bar: both need to know where the VISIBLE top of the
     page is, not where the viewport edge is. */
  var CHROME_SELECTOR =
    '.site-header-section, #shopify-section-header, [data-shm-chrome]';
  var chromeBottom = 0;
  var chromeListeners = [];

  function measureChromeBottom() {
    var chrome = document.querySelectorAll(CHROME_SELECTOR);
    var bottom = 0;
    for (var i = 0; i < chrome.length; i++) {
      var rect = chrome[i].getBoundingClientRect();
      /* "Overlays the viewport top" = starts at (or above) it and still
         reaches below it. A static header mid-scroll-out passes too, which
         is wanted: the pin point then rises as the header leaves. */
      if (rect.height > 0 && rect.top <= 1 && rect.bottom > bottom) {
        bottom = rect.bottom;
      }
    }
    return Math.round(bottom);
  }

  function initStickyOffset() {
    if (!document.querySelector('.pdp-layout')) return;
    var GAP = 16;
    var last = null;
    var queued = false;

    function measure() {
      queued = false;
      var bottom = measureChromeBottom();
      var top = bottom + GAP;
      if (top !== last) {
        last = top;
        document.documentElement.style.setProperty('--pdp-sticky-top', top + 'px');
        chromeBottom = bottom;
        for (var i = 0; i < chromeListeners.length; i++) chromeListeners[i](bottom);
      }
    }

    function queue() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(measure);
    }

    measure();
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    window.addEventListener('load', queue);
    /* site-header.js announces bar rewraps (menu overflowing to a 2nd row)
       with this event; the chrome height changes with it. */
    document.addEventListener('header:metrics-updated', queue);
  }

  /**
   * Mobile sticky buy bar (PDP and gift card).
   *
   * When the buttons row (wallet + Add to Bag + "More payment options", i.e.
   * `.pdp-product-form-add-to-cart-section`) scrolls out above the viewport,
   * THE SAME ELEMENT is pinned to the bottom of the screen; when the shopper
   * scrolls back up to where it belongs, it drops back into flow. Same
   * element on purpose — the old theme rendered a second copy
   * (`.product__btn-mob`) and toggled it on scroll, which meant two
   * `payment_button` instances, and a `<shopify-accelerated-checkout>` that
   * is moved or cloned after paint loses its iframe. Switching `position`
   * touches neither the DOM order nor the bindings: product.js's add
   * handler, the size/amount gate, the gift-card validation and the
   * add-to-bag feedback all keep working because nothing else changed.
   *
   * Mechanics:
   *   - a 1px sentinel is inserted right after the row, so it marks the
   *     row's ORIGINAL bottom edge whether or not the row is floating;
   *   - an IntersectionObserver on the sentinel: sentinel above the
   *     viewport = the row has left through the top -> float; sentinel back
   *     in view (or below) -> unfloat. Scroll handlers are not used;
   *   - while floating, an equal-height placeholder holds the row's slot so
   *     the page does not jump, and the document gets bottom padding equal
   *     to the bar so the last content is never hidden behind it;
   *   - mobile only (theme $small-down); crossing to desktop unfloats;
   *   - while any part of the footer is on screen the pinned bar slides out
   *     of view instead of covering it, and slides back when the footer
   *     leaves (a second IntersectionObserver, on the footer section).
   */
  function initStickyBuyBar() {
    var row = document.querySelector('.pdp-layout-v2 .pdp-product-form-add-to-cart-section');
    if (!row || !row.parentNode || !('IntersectionObserver' in window)) return;

    var FLOAT_CLASS = 'pdp-atb-sticky--floating';
    var BODY_CLASS = 'pdp-atb-sticky-active';
    var mobile = window.matchMedia('(max-width: 767px)');
    var placeholder = null;

    var sentinel = document.createElement('div');
    sentinel.className = 'pdp-atb-sticky__sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    row.parentNode.insertBefore(sentinel, row.nextSibling);

    function publishHeight() {
      document.documentElement.style.setProperty(
        '--pdp-atb-sticky-height',
        Math.round(row.getBoundingClientRect().height) + 'px'
      );
    }

    var HIDDEN_CLASS = 'pdp-atb-sticky--hidden';

    /* The two inputs the bar's state is computed from. Each observer only
       updates its own flag and calls apply(), so the two never race. */
    var rowGone = false;       /* the buttons row has scrolled out above */
    var footerVisible = false; /* any part of the footer is on screen */

    function floatRow() {
      if (placeholder) return;
      var height = row.getBoundingClientRect().height;
      placeholder = document.createElement('div');
      placeholder.className = 'pdp-atb-sticky__placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.style.height = height + 'px';
      row.parentNode.insertBefore(placeholder, row);
      row.classList.add(FLOAT_CLASS);
      publishHeight();
    }

    function unfloatRow() {
      if (!placeholder) return;
      row.classList.remove(FLOAT_CLASS);
      row.classList.remove(HIDDEN_CLASS);
      document.body.classList.remove(BODY_CLASS);
      placeholder.parentNode.removeChild(placeholder);
      placeholder = null;
    }

    /* While the footer is on screen the pinned bar slides down out of view
       (buttons.scss `--hidden`) instead of covering it, and slides back the
       moment the footer leaves. The bar stays `position: fixed` throughout
       so the slide can animate and the placeholder keeps the page's height
       steady; only the body's bottom clearance goes with it — that
       clearance exists so the last content can scroll clear of the bar, and
       with the bar gone at the footer it would just be a blank strip under
       the footer. Dropping it there cannot jolt the page: the footer is far
       taller than the clearance, so the scroll position is never past the
       new document end at that moment. */
    function setHidden(hidden) {
      row.classList.toggle(HIDDEN_CLASS, hidden);
      document.body.classList.toggle(BODY_CLASS, !hidden);
    }

    function apply() {
      if (!mobile.matches || !rowGone) {
        unfloatRow();
        return;
      }
      floatRow();
      setHidden(footerVisible);
    }

    function evaluate(entry) {
      /* The root's top is the VISIBLE top of the page — the viewport edge
         pushed down by the pinned header/search chrome (rootMargin below) —
         so "not intersecting AND above that edge" means the row has gone
         UNDER the chrome, not merely off the screen: that is the instant
         the buttons stop being visible, and the bar must appear then, with
         no dead window in between. A sentinel below the viewport (the
         shopper has not reached the buttons yet) is also "not intersecting"
         and must NOT float. */
      var edge = entry.rootBounds ? entry.rootBounds.top : 0;
      rowGone = !entry.isIntersecting && entry.boundingClientRect.top < edge;
      apply();
    }

    /* rootMargin cannot change on a live observer, so the observer is
       rebuilt whenever the measured chrome height changes (header rewrap,
       announcement strip, header scrolling out) — the sticky-column offset
       already tracks that and publishes it through chromeListeners.
       Rebuilding also yields a fresh entry, which is how a breakpoint flip
       re-evaluates. */
    var observer = null;
    function buildObserver() {
      if (observer) observer.disconnect();
      observer = new IntersectionObserver(function (entries) {
        evaluate(entries[entries.length - 1]);
      }, { threshold: 0, rootMargin: '-' + chromeBottom + 'px 0px 0px 0px' });
      observer.observe(sentinel);
    }
    buildObserver();
    chromeListeners.push(function () { buildObserver(); });

    /* The footer. `threshold: 0` fires the instant its top edge crosses the
       viewport bottom — which is exactly where the bar sits, so the bar is
       gone before it could cover a single pixel of footer — and again when
       the last pixel leaves. No rootMargin: the bar's own box must not be
       subtracted here, or the footer would count as visible only once it
       had already scrolled up past the bar. A theme without the footer
       section simply keeps today's behaviour. */
    var footer = document.getElementById('shopify-section-footer') || document.querySelector('footer');
    if (footer) {
      new IntersectionObserver(function (entries) {
        footerVisible = entries[entries.length - 1].isIntersecting;
        apply();
      }, { threshold: 0 }).observe(footer);
    }

    mobile.addEventListener('change', function () {
      apply();
      buildObserver();
    });
    window.addEventListener('resize', function () {
      if (placeholder) publishHeight();
    });
  }

  function init() {
    initStickyOffset();
    initReviewsJump();
    initAtbFeedback();
    initStickyBuyBar();
    if (!document.querySelector(GATE_SELECTOR)) return;

    /* Capture phase so the guard runs before the theme's own add-to-cart
       handler, which product.js binds on each `.pdp_main_product__add`
       (product.js L44). The wallet button matches neither selector, so the
       accelerated checkout flow is untouched. */
    document.addEventListener('click', guardAddClick, true);
    document.addEventListener('submit', guardSubmit, true);

    /* Bubble phase: by the time these run the radio has already toggled. Both
       are needed — re-clicking the chip that is already checked fires no
       change event, and keyboard arrow selection fires change without a click
       on a label. */
    /* Capture phase, so the snapshot exists before any click handler runs. */
    document.addEventListener('pointerdown', onGestureStart, true);
    document.addEventListener('keydown', onGestureStart, true);
    document.addEventListener('change', onSelection);
    document.addEventListener('click', onSelection);

    /* Deliberately NO variant-URL exception. Shopify appends ?variant= on almost
       every product link (and on every variant switch), so clearing the gate for
       it meant a size read as pre-selected on most entries to the page — the
       opposite of the requirement. The target only ever clears pristine on a real
       label click or radio change, which the listeners above cover. */
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
